import type { MetaIntegrationStatus, UserProfile } from '@/lib/types';

export interface PlatformConnections {
  /** Google Ads holds a working connection: a refresh token exists (#5936) and it isn't
   * known-revoked (#5935). Says nothing about *account selection* — that stays with
   * `googleAdsAccountAtom`; connected-but-unselected keeps the SELECT ACCOUNT flow. */
  googleConnected: boolean;
  /** The connection exists but the token is known-revoked (#5935) — show "reconnect"
   * wording instead of "connect". Always false when googleConnected is true. */
  googleBroken: boolean;
  /** Meta is usable for campaign work: entitled (test mode, #6257 — gated until Meta App
   * Review clears) + OAuth connected + an ad account selected, the same conditions the Meta
   * wizard gate enforces. When the entitlement is off, consumers render NO Meta surfaces —
   * prompts included. */
  metaConnected: boolean;
  /** Either signal is still resolving. Consumers must not flash a disconnected state
   * (walls, banners, disabled rows) while true — render neutral/loading instead. */
  isLoading: boolean;
}

export interface PlatformConnectionInputs {
  userProfile: UserProfile | undefined;
  isProfileLoading: boolean;
  /** The org may render Meta surfaces at all (test mode until Meta App Review clears, #6257). */
  metaEnabled: boolean;
  metaStatus: MetaIntegrationStatus | undefined;
  isMetaStatusLoading: boolean;
}

/**
 * Pure derivation behind ``usePlatformConnections`` (#5937), extracted so the per-platform
 * "connected" semantics are unit-testable without rendering. The rules:
 *
 * - Google is connected iff ``/user/me`` affirms ``google_ads_connected`` AND the token is not
 *   known-revoked (#5935). Absent fields (old cached profile, still loading) read as
 *   disconnected — ``isLoading`` is the signal consumers must use to avoid acting on that.
 * - Meta is connected iff the org is entitled (test mode, #6257) AND OAuth is connected AND an
 *   ad account is selected — the same conditions the Meta wizard gate enforces.
 */
/**
 * True when the Google Ads account picker should auto-open on Settings > Integrations (#5937):
 * the connection works and accounts are available, but none is selected — the card is unusable
 * without a selection. The Google mirror of ``shouldAutoOpenMetaAccountPicker`` (lib/meta.ts):
 * covers a fresh OAuth return (token just stored, 100+ accounts synced, nothing picked) and
 * stale connections from before the picker existed. Callers still gate on a once-per-session
 * ref; this is only the data-driven half of that decision.
 *
 * ``selectionResolved`` guards the hydration race (#5975): on a full reload
 * ``googleAdsAccountAtom`` is null until AppShell's selection effect runs, so "no selection"
 * cannot be trusted before ``googleAdsAccountResolvedAtom`` flips — without this gate the
 * picker pops on every browser refresh despite an active selection. Callers whose selection
 * signal is server-backed at render time (suspense-loaded membership) pass ``true``.
 */
export function shouldAutoOpenGoogleAccountPicker({
  googleConnected,
  hasAccounts,
  hasSelection,
  selectionResolved,
}: {
  googleConnected: boolean;
  hasAccounts: boolean;
  hasSelection: boolean;
  selectionResolved: boolean;
}): boolean {
  return selectionResolved && googleConnected && hasAccounts && !hasSelection;
}

export function derivePlatformConnections({
  userProfile,
  isProfileLoading,
  metaEnabled,
  metaStatus,
  isMetaStatusLoading,
}: PlatformConnectionInputs): PlatformConnections {
  const googleBroken = userProfile?.google_connection_broken === true;
  const googleConnected = userProfile?.google_ads_connected === true && !googleBroken;
  const metaConnected = metaEnabled && metaStatus?.is_connected === true && !!metaStatus.selected_ad_account;

  return {
    googleConnected,
    googleBroken,
    metaConnected,
    // The Meta status query is disabled outside the entitlement, so its loading flag only
    // counts while Meta surfaces can render at all.
    isLoading: isProfileLoading || (metaEnabled && isMetaStatusLoading),
  };
}
